import React, { useState } from 'react';
import styled from 'styled-components';
import IcoUser from '../assets/images/login/ico-id.svg';
import IcoPassword from '../assets/images/login/ico-password.svg';
import IcoKakao from '../assets/images/button/ico-kakao.png';
import BgLogin from '../assets/images/login/bg-login.svg';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import { useLogin } from '../hooks/RQuery/utils/auth';
import { kakaoLoginUrl, request } from '../service/common';
import { useNavigate } from 'react-router-dom';
import useSessionStorage from '../hooks/useSessionStorage';

const LoginSt = {
  Contain: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff url(${BgLogin}) no-repeat center;
    width: 100%;
    height: 100%;
  `,
  Wrap: styled.div`
    position: relative;
    width: 576px;
    background: #fff;
    box-shadow: 4px 4px 26px 2px #4b4a770d;
    border-radius: 10px;
    box-sizing: border-box;
    border: 1px solid #fff;
  `,
  Inner: styled.div`
    padding: 48px 48px 52px;
  `,
  Title: styled.strong`
    font-weight: 600;
    color: #263446;
    font-size: 24px;
    display: block;
    text-align: center;
    margin-bottom: 16px;
  `,
  Desc: styled.p`
    color: #8d97a4;
    font-size: 16px;
    font-weight: 600;
    padding-bottom: 32px;
    border-bottom: 1px solid #e8ebef;
    text-align: center;
    margin-bottom: 32px;
  `,
  LabelBox: styled.div`
    width: 100%;
  `,
  Label: styled.label<{ src: string }>`
    display: flex;
    align-items: center;
    color: #414d5e;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    &:before {
      content: '';
      width: 24px;
      height: 24px;
      margin-right: 2px;
      background: url(${(p) => (p.src ? p.src : '')}) no-repeat center;
    }
  `,
  Hr: styled.hr`
    display: block;
    border: 1px solid #e8ebef;
    margin: 24px 0;
  `,
  Button: styled.button`
    position: relative;
    border-radius: 8px;
    box-sizing: border-box;
    width: 100%;
    height: 62px;
    transition: 0.4s;
    overflow: hidden;
    &:before,
    &:after {
      position: absolute;
      left: 0;
      content: '';
      width: 100%;
      height: 100%;
      transition: 0.4s;
    }
    &:before {
      top: 0;
      background: linear-gradient(to right, #4b8ce7, #80c258);
    }
    &:after {
      top: -100%;
      background: linear-gradient(to right, #0077a6, #00bea3);
    }
    &:hover {
      &:before {
        top: 100%;
      }
      &:after {
        top: 0;
      }
    }
  `,
  ButtonText: styled.span`
    position: relative;
    z-index: 2;
    color: #fff;
    font-size: 18px;
    font-weight: 600;
  `,
  ButtonKaKao: styled.button`
    border-radius: 8px;
    box-sizing: border-box;
    width: 100%;
    height: 62px;
    transition: 0.4s;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.85);
    background-color: #fee500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 18px;
    &::before {
      content: '';
      width: 24px;
      height: 24px;
      background: url(${IcoKakao}) no-repeat center;
      background-size: contain;
    }
  `,
  Back: styled.button`
    position: absolute;
    right: 10px;
    top: 10px;
    width: 40px;
    height: 40px;
    &:before,
    &:after {
      position: absolute;
      content: '';
      width: 36px;
      height: 1px;
      background: #000;
      left: 50%;
      top: 50%;
    }
    &:before {
      transform: translate(-50%, -50%) rotate(45deg);
    }
    &:after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }
  `,
};

interface LoginProps {
  id: string;
  password: string;
  isError: boolean;
}

const Login = () => {
  const { set: roleSet } = useSessionStorage('role');
  const { set: memberIdSet } = useSessionStorage('memberId');
  const { set: nameSet } = useSessionStorage('name');
  const { set: tokenSet } = useSessionStorage('token');
  const navigate = useNavigate();
  const [form, setForm] = useState({
    loginId: '',
    password: '',
  });
  const [isError, setIsError] = useState({
    status: false,
    message: '',
  });
  const { mutate: mutateLogin } = useLogin();
  const onChange = (e) => {
    setIsError({ status: false, message: '' });
    setForm({ ...form, [e.target.id]: e.target.value });
  };
  const onLogin = () => {
    mutateLogin(form, {
      onSuccess: (data: any) => {
        tokenSet(data.accessToken);
        memberIdSet(data.memberId);
        nameSet(data.name);
        roleSet(data.role);
        // interceptor 작업 (토큰)
        request.interceptors.request.use((config) => {
          config.headers['Authorization'] = data.accessToken;
          return config;
        });
        navigate('/');
      },
      onError: (err: any) => {
        setIsError({ status: true, message: err.response.data.message });
      },
    });
  };

  const onKakaoLogin = () => {
    window.location.href = `https://kauth.kakao.com/oauth/authorize?client_id=12791a8fd5fa2290fd3bc43a6fd19ebb&redirect_uri=${kakaoLoginUrl}&response_type=code`;
  };

  const onBack = () => {
    navigate('/');
  };
  return (
    <LoginSt.Contain>
      <LoginSt.Wrap>
        <LoginSt.Back type={'button'} onClick={onBack}>
          <span className={'hidden'}>지도로 이동</span>
        </LoginSt.Back>
        <LoginSt.Inner>
          <LoginSt.Title>로그인</LoginSt.Title>
          <LoginSt.Desc>지오맵프로 이용을 위해 로그인 해주세요.</LoginSt.Desc>
          <LoginSt.LabelBox style={{ marginBottom: 38 }}>
            <LoginSt.Label htmlFor={'id'} src={IcoUser}>
              아이디
            </LoginSt.Label>
            <Input id={'loginId'} width={'100%'} placeholder={'아이디를 입력해주세요.'} onChange={onChange} />
          </LoginSt.LabelBox>
          <LoginSt.LabelBox style={{ marginBottom: 42 }}>
            <LoginSt.Label htmlFor={'password'} src={IcoPassword}>
              비밀번호
            </LoginSt.Label>
            <Input id={'password'} width={'100%'} placeholder={'비밀번호를 입력해주세요.'} $isError={isError.status} message={isError.message} onChange={onChange} />
          </LoginSt.LabelBox>
          <LoginSt.Button onClick={onLogin}>
            <LoginSt.ButtonText>로그인</LoginSt.ButtonText>
          </LoginSt.Button>
          <LoginSt.Hr />
          <LoginSt.ButtonKaKao onClick={onKakaoLogin}>카카오 로그인</LoginSt.ButtonKaKao>
        </LoginSt.Inner>
      </LoginSt.Wrap>
    </LoginSt.Contain>
  );
};

export default Login;
